/**
 * Takes an object and a name of one of its methods a copy of the method bound to the object.
 *
 * @param context -
 * @param methodName -
 * @returns Tcontext[Tmethod]
 */
export function bindToSelf<TContext, TMethod extends keyof TContext>(
    context: TContext,
    methodName: TMethod,
): TContext[TMethod] {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    return (context[methodName] as any as Function).bind(context);
}

/**
 * Executes a function only once. Cannot be reset.
 *
 * @param fn -
 * @returns () => void
 */
export function once(fn: () => void): () => void {
    let hasRun = false;
    return () => {
        if (!hasRun) {
            hasRun = true;
            fn();
        }
    };
}

export interface MemoizedFunction<TArgs extends any[], TReturns> {
    (...args: TArgs): TReturns;
    clear(): void;
}

/**
 * Memoize a given function
 *
 * @param fn - The function to memoize
 * @returns The memoized function
 */
export function memo<TArgs extends any[], TReturns>(
    fn: (...args: TArgs) => TReturns,
): MemoizedFunction<TArgs, TReturns> {
    let cache: { [key: string]: TReturns } = {};
    return Object.assign(
        (...args: any) => {
            const key = JSON.stringify(args);
            if (typeof cache[key] !== 'undefined') {
                return cache[key];
            } else {
                const value = fn(...args);
                cache[key] = value;
                return value;
            }
        },
        {
            clear: () => {
                cache = {};
            },
        },
    );
}
